import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ArrowLeftIcon, ChevronDownIcon, ExclamationTriangleIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import TimeTracking from '../components/TimeTracking'

interface TicketStatus {
  id: string
  name: string
  key?: string
  description?: string
  color: string
  requiresComment?: boolean
  requiresAssignee?: boolean
}

interface StaffUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface Priority {
  id: string
  name: string
  description?: string
  color?: string
  slaHours: number
  sortOrder: number
}

interface TicketComment {
  id: string
  content: string
  isInternal: boolean
  isSystemComment?: boolean
  type?: 'STATUS_CHANGE' | 'PRIORITY_CHANGE' | 'TITLE_CHANGE' | 'SYSTEM' | 'USER'
  createdAt: string
  author: {
    id: string
    firstName: string
    email: string
  }
}

interface SLACalculation {
  status: 'on_track' | 'at_risk' | 'overdue' | 'completed' | 'untracked'
  remainingHours: number
  elapsedHours: number
  slaTargetHours: number
  percentageComplete: number
  isAtRisk: boolean
  isOverdue: boolean
  shouldEscalate: boolean
  nextEscalationStatus?: string
}

interface TicketDetail {
  id: string
  ticketNumber: string
  title: string
  description: string
  status: TicketStatus
  priority: {
    id: string
    name: string
    description?: string
    color?: string
    slaHours: number
  }
  category: {
    id: string
    name: string
    description?: string
  }
  department: {
    id: string
    name: string
    description?: string
  }
  source?: {
    id: string
    name: string
    color?: string
    description?: string
  } | null
  clientOrg: {
    id: string
    name: string
    domain: string
  }
  clientUser: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  assignedUser?: {
    id: string
    firstName: string
    lastName: string
    email: string
  } | null
  clientService?: {
    id: string
    serviceNumber: string
    name: string
  } | null
  createdAt: string
  updatedAt: string
  dueDate?: string
  scheduledDate?: string
  estimatedHours?: number
  tags?: string[]
  metadata?: any
  closedAt?: string
}

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [ticket, setTicket] = useState<TicketDetail | null>(null)
  const [availableStatuses, setAvailableStatuses] = useState<TicketStatus[]>([])
  const [allowedTransitions, setAllowedTransitions] = useState<TicketStatus[]>([])
  const [availablePriorities, setAvailablePriorities] = useState<Priority[]>([])
  const [availableStaff, setAvailableStaff] = useState<StaffUser[]>([])
  const [availableServices, setAvailableServices] = useState<Array<{ id: string; serviceNumber: string; name: string }>>([])
  const [availableSources, setAvailableSources] = useState<Array<{ id: string; name: string; color?: string }>>([])
  const [isUpdatingSource, setIsUpdatingSource] = useState(false)
  const [comments, setComments] = useState<TicketComment[]>([])
  const [slaInfo, setSlaInfo] = useState<SLACalculation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false)
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState(false)
  const [isAddingComment, setIsAddingComment] = useState(false)
  const [isEscalating, setIsEscalating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false)
  const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false)
  const [commentForm, setCommentForm] = useState({
    content: '',
    isInternal: false
  })
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    statusId: '',
    comment: '',
    scheduledDate: ''
  })
  const [priorityUpdateForm, setPriorityUpdateForm] = useState({
    priorityId: '',
    comment: ''
  })
  const [assignmentForm, setAssignmentForm] = useState({
    assignedTo: ''
  })
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleInput, setTitleInput] = useState('')
  const [isSavingTitle, setIsSavingTitle] = useState(false)
  const [showTimeTrackingModal, setShowTimeTrackingModal] = useState(false)
  const [timeTrackingForm, setTimeTrackingForm] = useState({
    description: '',
    activityType: 'development',
    isBillable: true
  })
  const [isStartingTracking, setIsStartingTracking] = useState(false)
  const [timeEntries, setTimeEntries] = useState([])
  const [activeSession, setActiveSession] = useState(null)
  const [manualTimeForm, setManualTimeForm] = useState({
    description: '',
    durationMinutes: 30,
    activityType: 'development',
    isBillable: true
  })
  const [isAddingManualTime, setIsAddingManualTime] = useState(false)
  const [isUpdatingTags, setIsUpdatingTags] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [showTagInput, setShowTagInput] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [showServiceDropdown, setShowServiceDropdown] = useState(false)
  const [serviceForm, setServiceForm] = useState({ clientServiceId: '' })
  const [isUpdatingService, setIsUpdatingService] = useState(false)
  const [shouldTriggerFileInput, setShouldTriggerFileInput] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  
  // Trigger file input when shouldTriggerFileInput is true
  useEffect(() => {
    if (shouldTriggerFileInput && fileInputRef.current) {
      fileInputRef.current.click()
      setShouldTriggerFileInput(false)
    }
  }, [shouldTriggerFileInput])

  const isStaff = user?.roles.some(role => ['administrator', 'support_staff', 'staff', 'manager'].includes(role))

  const startEditTitle = () => {
    if (!ticket) return
    setTitleInput(ticket.title)
    setIsEditingTitle(true)
  }

  const cancelEditTitle = () => {
    setIsEditingTitle(false)
    setTitleInput('')
  }

  const saveTitle = async () => {
    if (!ticket || !titleInput.trim() || titleInput.trim() === ticket.title) {
      setIsEditingTitle(false)
      return
    }
    try {
      setIsSavingTitle(true)
      const response = await axios.patch(`/api/tickets/${ticket.id}`, { title: titleInput.trim() })
      if (response.data.success) {
        setTicket(response.data.data)
        setIsEditingTitle(false)
        toast.success('Title updated')
        // Refresh comments to show the title change system comment
        await refreshTicketData()
      } else {
        toast.error(response.data.error || 'Failed to update title')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update title')
    } finally {
      setIsSavingTitle(false)
    }
  }

  // Function to fetch time tracking data
  const fetchTimeTrackingData = async () => {
    if (!id) return
    
    try {
      const response = await axios.get(`/api/time-tracking/ticket/${id}`)
      setTimeEntries(response.data)
      
      // Check for active session
      const active = response.data.find((entry: any) => !entry.endTime)
      setActiveSession(active || null)
    } catch (error) {
      console.error('Error fetching time tracking data:', error)
    }
  }

  // Function to start time tracking
  const handleStartTracking = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !timeTrackingForm.description.trim()) return
    
    setIsStartingTracking(true)
    try {
      const response = await axios.post('/api/time-tracking/start', {
        ticketId: id,
        description: timeTrackingForm.description,
        activityType: timeTrackingForm.activityType,
        isBillable: timeTrackingForm.isBillable
      })
      
      if (response.data.success) {
        // Reset form and refresh data
        setTimeTrackingForm({
          description: '',
          activityType: 'development',
          isBillable: true
        })
        
        // Update the active session immediately
        setActiveSession(response.data.data)
        
        // Refresh time tracking data
        await fetchTimeTrackingData()
        
        // Close the modal
        setShowTimeTrackingModal(false)
        
        toast.success('Time tracking started!')
      }
    } catch (error) {
      console.error('Error starting time tracking:', error)
      toast.error('Failed to start time tracking')
    } finally {
      setIsStartingTracking(false)
    }
  }

  // Function to stop time tracking
  const handleStopTracking = async () => {
    if (!activeSession) return
    
    try {
      const response = await axios.post(`/api/time-tracking/stop/${activeSession.id}`)
      if (response.data.success) {
        // Clear active session immediately
        setActiveSession(null)
        
        // Refresh time tracking data
        await fetchTimeTrackingData()
        
        toast.success('Time tracking stopped!')
      }
    } catch (error) {
      console.error('Error stopping time tracking:', error)
      toast.error('Failed to stop time tracking')
    }
  }

  // Function to add manual time entry
  const handleManualTimeEntry = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !manualTimeForm.description.trim() || manualTimeForm.durationMinutes <= 0) return
    
    setIsAddingManualTime(true)
    try {
      const response = await axios.post('/api/time-tracking/manual', {
        ticketId: id,
        description: manualTimeForm.description,
        durationMinutes: manualTimeForm.durationMinutes,
        activityType: manualTimeForm.activityType,
        isBillable: manualTimeForm.isBillable
      })
      
      if (response.data.success) {
        // Reset form
        setManualTimeForm({
          description: '',
          durationMinutes: 30,
          activityType: 'development',
          isBillable: true
        })
        
        // Refresh time tracking data
        await fetchTimeTrackingData()
        
        toast.success('Time entry added successfully!')
      }
    } catch (error) {
      console.error('Error adding manual time entry:', error)
      toast.error('Failed to add time entry')
    } finally {
      setIsAddingManualTime(false)
    }
  }

  // Function to refresh ticket data
  const refreshTicketData = async () => {
    if (!id) return

    try {
      const [ticketResponse, commentsResponse, slaResponse] = await Promise.all([
        axios.get(`/api/tickets/${id}`),
        axios.get(`/api/tickets/${id}/comments`),
        axios.get(`/api/tickets/${id}/sla`)
      ])
      
      if (ticketResponse.data.success && commentsResponse.data.success && slaResponse.data.success) {
        setTicket(ticketResponse.data.data)
        setComments(commentsResponse.data.data || [])
        setSlaInfo(slaResponse.data.data)
        
        // Refresh allowed transitions if staff
        if (isStaff && ticketResponse.data.data.status) {
          try {
            const transitionsResponse = await axios.get(`/api/tickets/statuses/${ticketResponse.data.data.status.name}/transitions`)
            if (transitionsResponse.data.success) {
              setAllowedTransitions(transitionsResponse.data.data)
            }
          } catch (error) {
            console.error('Error fetching transitions:', error)
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing ticket data:', error)
    }
  }

  useEffect(() => {
    const fetchTicket = async () => {
      if (!id) {
        setError('Ticket ID not provided')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        
        const [ticketResponse, statusesResponse, prioritiesResponse, staffResponse, commentsResponse, slaResponse, servicesResponse, sourcesResponse] = await Promise.all([
          axios.get(`/api/tickets/${id}`),
          axios.get('/api/tickets/statuses'),
          axios.get('/api/tickets/priorities'),
          isStaff ? axios.get('/api/users/staff') : Promise.resolve({ data: { success: true, data: [] } }),
          axios.get(`/api/tickets/${id}/comments`),
          axios.get(`/api/tickets/${id}/sla`),
          axios.get('/api/services/client-services'),
          isStaff ? axios.get('/api/tickets/sources') : Promise.resolve({ data: { success: true, data: [] } })
        ])
        
        // Also fetch time tracking data
        await fetchTimeTrackingData()
        
        if (ticketResponse.data.success && statusesResponse.data.success && prioritiesResponse.data.success && staffResponse.data.success && commentsResponse.data.success && slaResponse.data.success) {
          setTicket(ticketResponse.data.data)
          setAvailableStatuses(statusesResponse.data.data)
          setAvailablePriorities(prioritiesResponse.data.data)
          setAvailableStaff(staffResponse.data.data || [])
          setAvailableSources(sourcesResponse.data.data || [])
          setComments(commentsResponse.data.data || [])
          setSlaInfo(slaResponse.data.data)
          
          // Set available services for the client organization
          if (ticketResponse.data.data.clientOrgId && servicesResponse.data.success) {
            const services = servicesResponse.data.data
              .filter((s: any) => s.clientOrgId === ticketResponse.data.data.clientOrgId)
              .map((s: any) => ({ id: s.id, serviceNumber: s.serviceNumber, name: s.name }))
            setAvailableServices(services)
          }
          
          // Fetch allowed transitions for current status
          if (isStaff && ticketResponse.data.data.status) {
            try {
              console.log('🔍 Fetching transitions for status:', ticketResponse.data.data.status.name)
              const transitionsResponse = await axios.get(`/api/tickets/statuses/${ticketResponse.data.data.status.name}/transitions`)
              if (transitionsResponse.data.success) {
                console.log('✅ Transitions fetched:', transitionsResponse.data.data)
                setAllowedTransitions(transitionsResponse.data.data)
              } else {
                console.log('❌ Transitions response not successful:', transitionsResponse.data)
              }
            } catch (error) {
              console.error('❌ Error fetching transitions:', error)
              // Fallback: show all available statuses if transitions fail
              setAllowedTransitions(statusesResponse.data.data)
            }
          }
        } else {
          setError('Failed to fetch ticket data')
        }
      } catch (error: any) {
        console.error('Error fetching ticket:', error)
        if (error.response?.status === 404) {
          setError('Ticket not found')
        } else {
          setError(error.response?.data?.error || 'Failed to fetch ticket')
        }
        toast.error('Failed to load ticket')
      } finally {
        setIsLoading(false)
      }
    }

    fetchTicket()
  }, [id])

  const handlePriorityUpdate = async (newPriorityId: string) => {
    if (!ticket || !isStaff) return

    // Check if comment is required
    const newPriority = availablePriorities.find(p => p.id === newPriorityId)
    if (!newPriority) return

    // For now, always require a comment for priority changes
    if (!priorityUpdateForm.comment.trim()) {
      toast.error('A comment is required for priority changes')
      return
    }

    try {
      setIsUpdatingPriority(true)
      
      const response = await axios.patch(`/api/tickets/${ticket.id}/priority`, {
        priorityId: newPriorityId,
        comment: priorityUpdateForm.comment
      })
      
      if (response.data.success) {
        // Update the local ticket state
        setTicket(prev => prev ? { ...prev, priority: newPriority } : null)
        // Reset the form
        setPriorityUpdateForm({ priorityId: '', comment: '' })
        toast.success('Ticket priority updated successfully')
        setShowPriorityDropdown(false)
        
        // Refresh ticket data to show the new comment
        await refreshTicketData()
      } else {
        toast.error(response.data.error || 'Failed to update priority')
      }
    } catch (error: any) {
      console.error('Error updating priority:', error)
      toast.error(error.response?.data?.error || 'Failed to update priority')
    } finally {
      setIsUpdatingPriority(false)
    }
  }

  const handleStatusUpdate = async (newStatusId: string) => {
    if (!ticket || !isStaff) return

    // Check if comment is required
    const newStatus = availableStatuses.find(s => s.id === newStatusId) || allowedTransitions.find(s => s.id === newStatusId)
    if (!newStatus) return
    const requiresComment = !!newStatus.requiresComment
    if (requiresComment && !statusUpdateForm.comment.trim()) {
      toast.error('A comment is required for this status change')
      return
    }

    // Determine if Work Scheduled is selected (using key or normalized name)
    const normalizedName = (newStatus.name || '').toLowerCase().replace(/\s+/g, '_')
    const isWorkScheduled = newStatus.key === 'work_scheduled' || normalizedName === 'work_scheduled'

    // If setting to work_scheduled, require scheduled date
    if (isWorkScheduled && !statusUpdateForm.scheduledDate) {
      toast.error('Scheduled date is required for Work Scheduled status')
      return
    }

    try {
      setIsUpdatingStatus(true)
      
      const response = await axios.patch(`/api/tickets/${ticket.id}/status`, {
        statusId: newStatusId,
        comment: statusUpdateForm.comment,
        scheduledDate: isWorkScheduled ? statusUpdateForm.scheduledDate : undefined
      })
      
      if (response.data.success) {
        // Update the local ticket state
        setTicket(prev => prev ? { ...prev, status: newStatus } : null)
        // Reset the form
        setStatusUpdateForm({ statusId: '', comment: '', scheduledDate: '' })
        toast.success('Ticket status updated successfully')
        setShowStatusDropdown(false)
        
        // Refresh ticket data to show the new comment
        await refreshTicketData()
      } else {
        toast.error(response.data.error || 'Failed to update status')
      }
    } catch (error: any) {
      console.error('Error updating status:', error)
      toast.error(error.response?.data?.error || 'Failed to update status')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleAssignmentUpdate = async (newAssignedUserId: string | null) => {
    if (!ticket || !isStaff) return

    try {
      setIsUpdatingAssignment(true)
      
      const response = await axios.patch(`/api/tickets/${ticket.id}/assign`, {
        assignedUserId: newAssignedUserId
      })
      
      if (response.data.success) {
        // Update the local ticket state
        const newAssignedUser = newAssignedUserId 
          ? availableStaff.find(s => s.id === newAssignedUserId) || null
          : null
        
        setTicket(prev => prev ? { ...prev, assignedUser: newAssignedUser } : null)
        toast.success(newAssignedUserId ? 'Ticket assigned successfully' : 'Ticket unassigned successfully')
        setShowAssignmentDropdown(false)
      } else {
        toast.error(response.data.error || 'Failed to update assignment')
      }
    } catch (error: any) {
      console.error('Error updating assignment:', error)
      toast.error(error.response?.data?.error || 'Failed to update assignment')
    } finally {
      setIsUpdatingAssignment(false)
    }
  }

  const handleSourceUpdate = async (sourceId: string) => {
    if (!ticket || !isStaff) return

    try {
      setIsUpdatingSource(true)
      const response = await axios.patch(`/api/tickets/${ticket.id}`, {
        sourceId: sourceId || null
      })

      if (response.data.success) {
        setTicket(response.data.data)
        toast.success('Contact method updated')
      } else {
        toast.error(response.data.error || 'Failed to update contact method')
      }
    } catch (error: any) {
      console.error('Error updating contact method:', error)
      toast.error(error.response?.data?.error || 'Failed to update contact method')
    } finally {
      setIsUpdatingSource(false)
    }
  }

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ticket || !commentForm.content.trim()) return

    try {
      setIsAddingComment(true)
      
      const response = await axios.post(`/api/tickets/${ticket.id}/comments`, commentForm)
      
      console.log('Comment creation response:', response.data)
      
      if (response.data.success) {
        // Ensure the new comment has all required fields
        const newComment = {
          ...response.data.data,
          // Set default values for missing fields
          type: response.data.data.type || 'USER',
          isSystemComment: response.data.data.isSystemComment || false,
          // Ensure author object exists
          author: response.data.data.author || {
            firstName: user?.firstName || 'User',
            lastName: user?.lastName || '',
            email: user?.email || ''
          }
        }
        
        console.log('Adding new comment:', newComment)
        
        // Add the new comment to the beginning of the array (newest first)
        setComments(prev => [newComment, ...prev])
        // Reset the form
        setCommentForm({ content: '', isInternal: false })
        toast.success('Comment added successfully')
      } else {
        toast.error(response.data.error || 'Failed to add comment')
      }
    } catch (error: any) {
      console.error('Error adding comment:', error)
      toast.error(error.response?.data?.error || 'Failed to add comment')
    } finally {
      setIsAddingComment(false)
    }
  }

  const handleEscalation = async () => {
    if (!ticket || !isStaff) return

    try {
      setIsEscalating(true)
      
      const response = await axios.post(`/api/tickets/${ticket.id}/escalate`)
      
      if (response.data.success) {
        // Update the local ticket state with new status
        if (response.data.data.ticket) {
          setTicket(response.data.data.ticket)
        }
        // Update SLA info
        if (response.data.data.sla) {
          setSlaInfo(response.data.data.sla)
        }
        toast.success(response.data.message || 'Ticket escalated successfully')
      } else {
        toast.error(response.data.error || 'Failed to escalate ticket')
      }
    } catch (error: any) {
      console.error('Error escalating ticket:', error)
      toast.error(error.response?.data?.error || 'Failed to escalate ticket')
    } finally {
      setIsEscalating(false)
    }
  }

  // Tag management functions
  const handleAddTag = async () => {
    if (!ticket || !isStaff || !newTag.trim()) return
    
    const tagToAdd = newTag.trim().toLowerCase()
    
    // Check if tag already exists
    if (ticket.tags && ticket.tags.includes(tagToAdd)) {
      toast.error('Tag already exists')
      setNewTag('')
      return
    }
    
    try {
      setIsUpdatingTags(true)
      const updatedTags = [...(ticket.tags || []), tagToAdd]
      
      const response = await axios.patch(`/api/tickets/${ticket.id}`, {
        tags: updatedTags
      })
      
      if (response.data.success) {
        setTicket(prev => prev ? { ...prev, tags: updatedTags } : null)
        setNewTag('')
        setShowTagInput(false)
        toast.success('Tag added successfully')
      }
    } catch (error: any) {
      console.error('Error adding tag:', error)
      toast.error(error.response?.data?.error || 'Failed to add tag')
    } finally {
      setIsUpdatingTags(false)
    }
  }

  const handleRemoveTag = async (tagToRemove: string) => {
    if (!ticket || !isStaff) return
    
    try {
      setIsUpdatingTags(true)
      const updatedTags = (ticket.tags || []).filter(tag => tag !== tagToRemove)
      
      const response = await axios.patch(`/api/tickets/${ticket.id}`, {
        tags: updatedTags
      })
      
      if (response.data.success) {
        setTicket(prev => prev ? { ...prev, tags: updatedTags } : null)
        toast.success('Tag removed successfully')
      }
    } catch (error: any) {
      console.error('Error removing tag:', error)
      toast.error(error.response?.data?.error || 'Failed to remove tag')
    } finally {
      setIsUpdatingTags(false)
    }
  }

  // File attachment functions
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!ticket || !isStaff || !e.target.files || e.target.files.length === 0) return
    
    const file = e.target.files[0]
    
    try {
      setIsUploadingAttachment(true)
      
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await axios.post(`/api/tickets/${ticket.id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      if (response.data.success) {
        setAttachments(prev => [...prev, response.data.data])
        toast.success('File uploaded successfully')
        e.target.value = '' // Reset input
      }
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error(error.response?.data?.error || 'Failed to upload file')
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!isStaff) return
    
    try {
      await axios.delete(`/api/tickets/attachments/${attachmentId}`)
      setAttachments(prev => prev.filter((a: any) => a.id !== attachmentId))
      toast.success('File deleted successfully')
    } catch (error: any) {
      console.error('Error deleting file:', error)
      toast.error(error.response?.data?.error || 'Failed to delete file')
    }
  }

  const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
    try {
      const response = await axios.get(`/api/tickets/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    } catch (error: any) {
      console.error('Error downloading file:', error)
      toast.error('Failed to download file')
    }
  }

  const handleServiceUpdate = async (newServiceId: string | null) => {
    if (!ticket || !isStaff) return
    
    try {
      setIsUpdatingService(true)
      
      const response = await axios.patch(`/api/tickets/${ticket.id}`, {
        clientServiceId: newServiceId || null
      })
      
      if (response.data.success) {
        // Update the local ticket state
        const newService = newServiceId 
          ? availableServices.find(s => s.id === newServiceId) || null
          : null
        
        setTicket(prev => prev ? { ...prev, clientService: newService } : null)
        toast.success(newServiceId ? 'Service associated successfully' : 'Service removed successfully')
        setShowServiceDropdown(false)
      } else {
        toast.error(response.data.error || 'Failed to update service')
      }
    } catch (error: any) {
      console.error('Error updating service:', error)
      toast.error(error.response?.data?.error || 'Failed to update service')
    } finally {
      setIsUpdatingService(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSLAStatusColor = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 bg-green-100'
      case 'at_risk':
        return 'text-yellow-600 bg-yellow-100'
      case 'overdue':
        return 'text-red-600 bg-red-100'
      case 'completed':
        return 'text-gray-600 bg-gray-100'
      case 'untracked':
        return 'text-gray-600 bg-gray-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getSLAStatusText = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'On Track'
      case 'at_risk':
        return 'At Risk'
      case 'overdue':
        return 'Overdue'
      case 'completed':
        return 'Completed'
      case 'untracked':
        return 'Untracked'
      default:
        return 'Unknown'
    }
  }

  const formatHours = (hours: number) => {
    if (hours < 1) {
      const minutes = Math.round(hours * 60)
      return `${minutes}m`
    }
    
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    
    if (minutes === 0) {
      return `${wholeHours}h`
    }
    
    return `${wholeHours}h ${minutes}m`
  }

  const getStatusColor = (status: TicketStatus) => {
    // Use the color from the status object if available
    if (status.color) {
      // Return inline styles for dynamic colors
      return ''
    }
    
    // Fallback to hardcoded colors if no color is provided
    const statusColors: { [key: string]: string } = {
      'new': 'bg-blue-100 text-blue-800',
      'internal_analysis': 'bg-purple-100 text-purple-800',
      'work_in_progress': 'bg-green-100 text-green-800',
      'work_scheduled': 'bg-yellow-100 text-yellow-800',
      'awaiting_third_party': 'bg-red-100 text-red-800',
      'awaiting_customer_response': 'bg-purple-100 text-purple-800',
      'pending_closure': 'bg-gray-100 text-gray-800',
      'closed_complete': 'bg-green-100 text-green-800'
    }
    return statusColors[status.name] || 'bg-gray-100 text-gray-800'
  }

  const getStatusStyle = (status: TicketStatus) => {
    if (status.color) {
      // Calculate background color with opacity
      const bgColor = status.color + '20' // 20% opacity
      return {
        backgroundColor: bgColor,
        color: status.color
      }
    }
    return {}
  }

  const getPriorityColor = (priorityName: string) => {
    const priorityColors: { [key: string]: string } = {
      'low': 'bg-gray-100 text-gray-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-yellow-100 text-yellow-800',
      'urgent': 'bg-red-100 text-red-800'
    }
    return priorityColors[priorityName] || 'bg-gray-100 text-gray-800'
  }

  const handleBackToTickets = () => {
    navigate('/tickets')
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ticket...</p>
        </div>
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-red-900">Error loading ticket</h3>
          <p className="mt-2 text-sm text-red-500">{error}</p>
          <div className="mt-4 space-x-3">
            <button 
              className="btn-secondary"
              onClick={handleBackToTickets}
            >
              Back to Tickets
            </button>
            <button 
              className="btn-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBackToTickets}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Tickets
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            {isEditingTitle ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  className="text-3xl font-semibold text-gray-900 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={saveTitle}
                  disabled={isSavingTitle || !titleInput.trim()}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={cancelEditTitle}
                  disabled={isSavingTitle}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
            <h1 className="text-3xl font-semibold text-gray-900">{ticket.title}</h1>
                {isStaff && (
                  <button
                    onClick={startEditTitle}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    title="Edit title"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
            <p className="mt-1 text-sm text-gray-500">{ticket.ticketNumber}</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Status Dropdown for Staff */}
            {isStaff ? (
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={isUpdatingStatus}
                  className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(ticket.status)} hover:opacity-80 transition-opacity`}
                  style={getStatusStyle(ticket.status)}
                >
                  {ticket.status.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                </button>
                
                {showStatusDropdown && (
                  <>
                    {/* Overlay to close dropdown when clicking outside */}
                    <div 
                      className="fixed inset-0 z-[5]" 
                      onClick={() => setShowStatusDropdown(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Update Status</h4>
                      
                      {/* Status Selection */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                        <select
                          value={statusUpdateForm.statusId}
                          onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, statusId: e.target.value }))}
                          className="input-field w-full"
                        >
                          <option value="">Select new status</option>
                          {allowedTransitions.length > 0 ? (
                            allowedTransitions.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No status transitions available</option>
                          )}
                        </select>
                        {allowedTransitions.length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Debug: {allowedTransitions.length} transitions available
                          </p>
                        )}
                      </div>
                      
                      {/* Comment (conditional requirement) */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {(() => {
                            const selectedStatus = availableStatuses.find(s => s.id === statusUpdateForm.statusId) || allowedTransitions.find(s => s.id === statusUpdateForm.statusId)
                            const requiresComment = !!selectedStatus?.requiresComment
                            return `Comment${requiresComment ? ' *' : ''}`
                          })()}
                        </label>
                        <textarea
                          value={statusUpdateForm.comment}
                          onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, comment: e.target.value }))}
                          rows={3}
                          className="input-field w-full"
                          placeholder="Explain why the status is changing..."
                        />
                      </div>

                      {/* Scheduled Date for Work Scheduled Status */}
                      {(() => {
                        const selectedStatus = availableStatuses.find(s => s.id === statusUpdateForm.statusId) || allowedTransitions.find(s => s.id === statusUpdateForm.statusId)
                        const isWorkScheduled = selectedStatus && (selectedStatus.name === 'work_scheduled' || selectedStatus.name === 'Work Scheduled' || selectedStatus.name?.toLowerCase().replace(/\s+/g, '_') === 'work_scheduled')
                        return isWorkScheduled
                      })() && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time *</label>
                          <input
                            type="datetime-local"
                            value={statusUpdateForm.scheduledDate}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                            className="input-field w-full"
                            required
                            min={new Date().toISOString().slice(0, 16)}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This will add the ticket to the calendar for the scheduled work
                          </p>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleStatusUpdate(statusUpdateForm.statusId)}
                          disabled={(() => {
                            const selectedStatus = availableStatuses.find(s => s.id === statusUpdateForm.statusId) || allowedTransitions.find(s => s.id === statusUpdateForm.statusId)
                            const isWorkScheduled = selectedStatus && (selectedStatus.name === 'work_scheduled' || selectedStatus.name === 'Work Scheduled' || selectedStatus.name?.toLowerCase().replace(/\s+/g, '_') === 'work_scheduled')
                            const requiresComment = !!selectedStatus?.requiresComment
                            return isUpdatingStatus || !statusUpdateForm.statusId || (requiresComment && !statusUpdateForm.comment.trim()) || (isWorkScheduled && !statusUpdateForm.scheduledDate)
                          })()}
                          className="btn-primary text-sm py-2 px-3"
                        >
                          {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                        </button>
                        <button
                          onClick={() => setShowStatusDropdown(false)}
                          className="btn-secondary text-sm py-2 px-3"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                  </>
                )}
              </div>
            ) : (
              <span 
                className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(ticket.status)}`}
                style={getStatusStyle(ticket.status)}
              >
                {ticket.status.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            )}
            
            {/* Priority Display/Dropdown for Staff */}
            {isStaff ? (
              <div className="relative">
                <button
                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                  disabled={isUpdatingPriority}
                  className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(ticket.priority.name)} hover:opacity-80 transition-opacity`}
                >
                  {ticket.priority.name.charAt(0).toUpperCase() + ticket.priority.name.slice(1)} Priority
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                </button>
                
                {showPriorityDropdown && (
                  <>
                    {/* Overlay to close dropdown when clicking outside */}
                    <div 
                      className="fixed inset-0 z-[5]" 
                      onClick={() => setShowPriorityDropdown(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Update Priority</h4>
                      
                      {/* Priority Selection */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Priority</label>
                        <select
                          value={priorityUpdateForm.priorityId}
                          onChange={(e) => setPriorityUpdateForm(prev => ({ ...prev, priorityId: e.target.value }))}
                          className="input-field w-full"
                        >
                          <option value="">Select new priority</option>
                          {availablePriorities.map((priority) => (
                            <option key={priority.id} value={priority.id}>
                              {priority.name.charAt(0).toUpperCase() + priority.name.slice(1)} ({priority.slaHours}h SLA)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Comment Required */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Comment Required *</label>
                        <textarea
                          value={priorityUpdateForm.comment}
                          onChange={(e) => setPriorityUpdateForm(prev => ({ ...prev, comment: e.target.value }))}
                          rows={3}
                          className="input-field w-full"
                          placeholder="Explain why the priority is changing..."
                          required
                        />
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handlePriorityUpdate(priorityUpdateForm.priorityId)}
                          disabled={isUpdatingPriority || !priorityUpdateForm.priorityId || !priorityUpdateForm.comment.trim()}
                          className="btn-primary text-sm py-2 px-3"
                        >
                          {isUpdatingPriority ? 'Updating...' : 'Update Priority'}
                        </button>
                        <button
                          onClick={() => setShowPriorityDropdown(false)}
                          className="btn-secondary text-sm py-2 px-3"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                  </>
                )}
              </div>
            ) : (
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(ticket.priority.name)}`}>
                {ticket.priority.name.charAt(0).toUpperCase() + ticket.priority.name.slice(1)} Priority
              </span>
            )}
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          </div>

          {/* Comments Section */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Comments & Activity</h3>
            
            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="mb-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="comment-content" className="block text-sm font-medium text-gray-700 mb-2">
                    Add Comment
                  </label>
                  <textarea
                    id="comment-content"
                    rows={3}
                    className="input-field w-full break-words resize-y"
                    placeholder="Type your comment here..."
                    value={commentForm.content}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                    disabled={isAddingComment}
                    required
                  />
                </div>
                
                {isStaff && (
                  <div className="flex items-center">
                    <input
                      id="comment-internal"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={commentForm.isInternal}
                      onChange={(e) => setCommentForm(prev => ({ ...prev, isInternal: e.target.checked }))}
                      disabled={isAddingComment}
                    />
                    <label htmlFor="comment-internal" className="ml-2 text-sm text-gray-700">
                      Internal comment (staff only)
                    </label>
                  </div>
                )}
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isAddingComment || !commentForm.content.trim()}
                  >
                    {isAddingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No comments yet. Be the first to add a comment!</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Comments ({comments.length})</h3>
                  </div>
                  {comments.map((comment, index) => {
                    try {
                      return (
                        <div
                          key={comment.id}
                                                className={`p-4 rounded-lg border ${
                        comment.type === 'STATUS_CHANGE'
                          ? 'bg-blue-50 border-blue-200'
                          : comment.type === 'PRIORITY_CHANGE'
                          ? 'bg-orange-50 border-orange-200'
                          : comment.isInternal 
                          ? 'bg-purple-50 border-purple-200' 
                          : 'bg-gray-50 border-gray-200'
                      } ${index === 0 ? 'ring-2 ring-blue-200' : ''}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">
                                {comment.author?.firstName && comment.author?.lastName
                                  ? `${comment.author.firstName} ${comment.author.lastName}`
                                  : (comment.author?.firstName || comment.author?.email || 'Unknown User')}
                              </span>
                              {comment.isInternal && (
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  Internal
                                </span>
                              )}
                                                        {comment.type === 'STATUS_CHANGE' && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              Status Change
                            </span>
                          )}
                          {comment.type === 'PRIORITY_CHANGE' && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              Priority Change
                            </span>
                          )}
                          {comment.isSystemComment && comment.type !== 'STATUS_CHANGE' && comment.type !== 'PRIORITY_CHANGE' && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              System
                            </span>
                          )}
                              {index === 0 && (
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  Latest
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>
                         <div className="text-sm text-gray-700 whitespace-pre-wrap break-words overflow-x-hidden">
                            {comment.content}
                          </div>
                        </div>
                      )
                    } catch (error) {
                      console.error('Error rendering comment:', error, comment)
                      return (
                        <div key={comment.id} className="p-4 rounded-lg border border-red-200 bg-red-50">
                          <p className="text-sm text-red-600">Error rendering comment</p>
                          <p className="text-xs text-red-500 mt-1">ID: {comment.id}</p>
                        </div>
                      )
                    }
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Assignment Section */}
          <div className="card relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Assignment</h3>
              {isStaff && (
                <button
                  onClick={() => setShowAssignmentDropdown(!showAssignmentDropdown)}
                  disabled={isUpdatingAssignment}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {ticket.assignedUser ? 'Reassign' : 'Assign'}
                </button>
              )}
            </div>
            
            {ticket.assignedUser ? (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium text-sm">
                      {ticket.assignedUser.firstName.charAt(0)}{ticket.assignedUser.lastName.charAt(0)}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {ticket.assignedUser.firstName} {ticket.assignedUser.lastName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{ticket.assignedUser.email}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-400 font-medium text-sm">?</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500">Unassigned</p>
                  <p className="text-sm text-gray-400">No user assigned</p>
                </div>
              </div>
            )}
            
            {/* Assignment Dropdown for Staff */}
            {isStaff && showAssignmentDropdown && (
              <>
                {/* Overlay to close dropdown when clicking outside */}
                <div 
                  className="fixed inset-0 z-[5]" 
                  onClick={() => setShowAssignmentDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Assign To</h4>
                  
                  {/* Staff Selection */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Staff</label>
                    <select
                      value={assignmentForm.assignedTo}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="input-field w-full text-sm"
                    >
                      <option value="">Unassigned</option>
                      {availableStaff.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.firstName} {staff.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAssignmentUpdate(assignmentForm.assignedTo)}
                      disabled={isUpdatingAssignment}
                      className="btn-primary text-sm py-2 px-3 flex-1"
                    >
                      {isUpdatingAssignment ? 'Updating...' : 'Update'}
                    </button>
                    <button
                      onClick={() => setShowAssignmentDropdown(false)}
                      className="btn-secondary text-sm py-2 px-3 flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              </>
            )}
          </div>

          {/* Ticket Information */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Ticket Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="text-sm text-gray-900">{formatDate(ticket.createdAt)}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="text-sm text-gray-900">{formatDate(ticket.updatedAt)}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Department</dt>
                <dd className="text-sm text-gray-900">{ticket.department.name}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="text-sm text-gray-900">{ticket.category.name}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Method of Contact</dt>
                <dd className="text-sm text-gray-900 mt-1">
                  {isStaff ? (
                    <select
                      value={ticket.source?.id || ''}
                      onChange={(e) => handleSourceUpdate(e.target.value)}
                      disabled={isUpdatingSource}
                      className="input-field w-full text-sm"
                    >
                      <option value="">Not specified</option>
                      {availableSources.map((source) => (
                        <option key={source.id} value={source.id}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  ) : ticket.source ? (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: ticket.source.color ? `${ticket.source.color}20` : '#E5E7EB',
                        color: ticket.source.color || '#6B7280'
                      }}
                    >
                      {ticket.source.name}
                    </span>
                  ) : (
                    <span className="text-gray-500">Not specified</span>
                  )}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">SLA Target</dt>
                <dd className="text-sm text-gray-900">{slaInfo?.status === 'untracked' ? 'Untracked' : `${ticket.priority.slaHours} hours`}</dd>
              </div>
              
              {ticket.scheduledDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Scheduled Date</dt>
                  <dd className="text-sm text-gray-900">
                    <span className="font-medium text-blue-600">
                      {formatDate(ticket.scheduledDate)}
                    </span>
                  </dd>
                </div>
              )}
              
              {ticket.dueDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                  <dd className="text-sm text-gray-900">{formatDate(ticket.dueDate)}</dd>
                </div>
              )}
              
              {ticket.estimatedHours && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Estimated Hours</dt>
                  <dd className="text-sm text-gray-900">{ticket.estimatedHours} hours</dd>
                </div>
              )}
              
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">Tags</dt>
                <dd className="text-sm text-gray-900">
                  <div className="flex flex-wrap gap-2 items-center">
                    {ticket.tags && ticket.tags.length > 0 && ticket.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                        {isStaff && (
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            disabled={isUpdatingTags}
                            className="hover:text-blue-600 disabled:opacity-50"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                    {isStaff && (
                      <>
                        {showTagInput ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  handleAddTag()
                                } else if (e.key === 'Escape') {
                                  setShowTagInput(false)
                                  setNewTag('')
                                }
                              }}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="Enter tag name"
                              autoFocus
                            />
                            <button
                              onClick={handleAddTag}
                              disabled={isUpdatingTags || !newTag.trim()}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => {
                                setShowTagInput(false)
                                setNewTag('')
                              }}
                              className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowTagInput(true)}
                            className="px-2 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            + Add Tag
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </dd>
              </div>
              
              {ticket.metadata && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Additional Info</dt>
                  <dd className="text-sm text-gray-900">
                    {ticket.metadata.serviceType && (
                      <div className="mb-2">
                        <span className="font-medium">Service Type:</span> {ticket.metadata.serviceType}
                      </div>
                    )}
                    {ticket.metadata.customFields && Object.keys(ticket.metadata.customFields).length > 0 && (
                      <div>
                        <span className="font-medium">Custom Fields:</span>
                        <div className="mt-1 space-y-1">
                          {Object.entries(ticket.metadata.customFields).map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="text-gray-600">{key}:</span> {String(value)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Attachments Section (Staff Only) */}
          {isStaff && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
                <label
                  htmlFor="file-input-attachment"
                  className={`px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded cursor-pointer hover:bg-blue-50 inline-block ${isUploadingAttachment ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isUploadingAttachment ? 'Uploading...' : '+ Add File'}
                </label>
                <input
                  id="file-input-attachment"
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => {
                    console.log('File input changed, files:', e.target.files)
                    handleFileUpload(e)
                  }}
                  disabled={isUploadingAttachment}
                  className="hidden"
                  accept="*/*"
                />
              </div>
              
              {attachments.length === 0 ? (
                <p className="text-sm text-gray-500">No attachments</p>
              ) : (
                <div className="space-y-2">
                  {attachments.map((attachment: any) => {
                    const maxLength = 30
                    const displayName = attachment.originalFilename || attachment.filename
                    const truncatedName = displayName.length > maxLength 
                      ? displayName.substring(0, maxLength - 3) + '...' 
                      : displayName
                    
                    return (
                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <button
                          onClick={() => handleDownloadAttachment(attachment.id, displayName)}
                          className="flex-1 text-left text-sm text-blue-600 hover:underline truncate"
                          title={displayName}
                        >
                          📎 {truncatedName}
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          className="ml-2 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Time Tracking - Compact Sidebar Version (Staff Only) */}
          {isStaff && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Time Tracking</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total:</span>
                  <span className="font-medium text-gray-900">
                    {Math.floor(timeEntries.reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) / 60)}h {timeEntries.reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) % 60}m
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Billable:</span>
                  <span className="font-medium text-gray-900">
                    {Math.floor(timeEntries.filter((entry: any) => entry.isBillable).reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) / 60)}h {timeEntries.filter((entry: any) => entry.isBillable).reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) % 60}m
                  </span>
                </div>
                {activeSession ? (
                  <button
                    onClick={handleStopTracking}
                    className="w-full px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Stop Tracking
                  </button>
                ) : (
                  <button
                    onClick={() => setShowTimeTrackingModal(true)}
                    className="w-full px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Start Tracking
                  </button>
                )}
              </div>
            </div>
          )}

          {/* SLA Information */}
          {slaInfo && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">SLA Status</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSLAStatusColor(slaInfo.status)}`}>
                      {getSLAStatusText(slaInfo.status)}
                    </span>
                  </dd>
                </div>
                
                {slaInfo.status !== 'untracked' ? (
                  <>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Time Elapsed</dt>
                  <dd className="text-sm text-gray-900">{formatHours(slaInfo.elapsedHours)}</dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">Time Remaining</dt>
                  <dd className="text-sm text-gray-900">{formatHours(slaInfo.remainingHours)}</dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">Progress</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          slaInfo.status === 'overdue' ? 'bg-red-500' :
                          slaInfo.status === 'at_risk' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                            style={{ width: `${Math.min(100, Math.max(0, slaInfo.percentageComplete))}%` }}
                      ></div>
                    </div>
                        <span className="text-xs text-gray-500 mt-1 block">{Math.round(slaInfo.percentageComplete)}%</span>
                  </dd>
                </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-600">SLA tracking is not applied to this ticket.</div>
                )}

                {/* Escalation Controls for Staff */}
                {isStaff && slaInfo.shouldEscalate && (
                  <div className="pt-3 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-500 mb-2">Auto-escalation</dt>
                    <dd className="text-sm">
                      {slaInfo.nextEscalationStatus && (
                        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                          <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                          Ticket can be escalated to: <strong>{slaInfo.nextEscalationStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                        </div>
                      )}
                      <button
                        onClick={handleEscalation}
                        disabled={isEscalating}
                        className="w-full btn-secondary text-sm py-2"
                      >
                        {isEscalating ? 'Escalating...' : 'Trigger Escalation'}
                      </button>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Client Information */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Client Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Organization</dt>
                <dd className="text-sm text-gray-900">{ticket.clientOrg.name}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Contact</dt>
                <dd className="text-sm text-gray-900">
                  {ticket.clientUser.firstName} {ticket.clientUser.lastName}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="text-sm text-gray-900">{ticket.clientUser.email}</dd>
              </div>
            </dl>
          </div>

          {/* Service Association Section (Staff Only) */}
          <div className="card relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Associated Service</h3>
              {isStaff && (
                <button
                  onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                  disabled={isUpdatingService}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {ticket.clientService ? 'Change' : 'Assign'}
                </button>
              )}
            </div>
            
            {ticket.clientService ? (
              <div>
                <p className="text-sm text-gray-900">
                  {ticket.clientService.serviceNumber} - {ticket.clientService.name}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No service assigned</p>
            )}
            
            {/* Service Dropdown for Staff */}
            {isStaff && showServiceDropdown && (
              <div className="mt-3">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Assign Service</h4>
                
                {/* Service Selection */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Service</label>
                  <select
                    value={serviceForm.clientServiceId}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, clientServiceId: e.target.value }))}
                    className="input-field w-full text-sm"
                  >
                    <option value="">No Service</option>
                    {availableServices.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.serviceNumber} - {service.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleServiceUpdate(serviceForm.clientServiceId)}
                    disabled={isUpdatingService}
                    className="btn-primary text-sm py-2 px-3 flex-1"
                  >
                    {isUpdatingService ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    onClick={() => setShowServiceDropdown(false)}
                    className="btn-secondary text-sm py-2 px-3 flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>


        </div>
      </div>

      {/* Time Tracking Modal (Staff Only) */}
      {showTimeTrackingModal && isStaff && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Time Tracking</h3>
                <button
                  onClick={() => setShowTimeTrackingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Simple Time Tracking Interface */}
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Time</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {Math.floor(timeEntries.reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) / 60)}h {timeEntries.reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) % 60}m
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Billable Time</p>
                    <p className="text-xl font-semibold text-green-600">
                      {Math.floor(timeEntries.filter((entry: any) => entry.isBillable).reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) / 60)}h {timeEntries.filter((entry: any) => entry.isBillable).reduce((total, entry: any) => total + (entry.durationMinutes || 0), 0) % 60}m
                    </p>
                  </div>
                </div>

                {/* Active Session or Start Form */}
                {activeSession ? (
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h4 className="text-md font-medium text-blue-900 mb-3">Active Session</h4>
                    <p className="text-sm text-blue-700 mb-3">{activeSession.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-600">
                        Started: {new Date(activeSession.startTime).toLocaleTimeString()}
                      </span>
                      <button
                        onClick={handleStopTracking}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Stop Tracking
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Start New Session</h4>
                    <form onSubmit={handleStartTracking} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          rows={3}
                          value={timeTrackingForm.description}
                          onChange={(e) => setTimeTrackingForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="What are you working on?"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Activity Type
                          </label>
                          <select 
                            value={timeTrackingForm.activityType}
                            onChange={(e) => setTimeTrackingForm(prev => ({ ...prev, activityType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="development">Development</option>
                            <option value="research">Research</option>
                            <option value="communication">Communication</option>
                            <option value="testing">Testing</option>
                            <option value="deployment">Deployment</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="billable"
                            checked={timeTrackingForm.isBillable}
                            onChange={(e) => setTimeTrackingForm(prev => ({ ...prev, isBillable: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="billable" className="ml-2 text-sm text-gray-700">
                            Billable
                          </label>
                        </div>
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isStartingTracking}
                        className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        {isStartingTracking ? 'Starting...' : 'Start Tracking'}
                      </button>
                    </form>
                  </div>
                )}

                {/* Recent Time Entries */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Recent Time Entries</h4>
                  {timeEntries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No time entries yet. Start tracking to see your sessions here.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {timeEntries.slice(0, 5).map((entry: any) => (
                        <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-medium text-gray-900">{entry.description}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              entry.isBillable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {entry.isBillable ? 'Billable' : 'Non-billable'}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>{entry.activityType}</span>
                            <span>{Math.floor((entry.durationMinutes || 0) / 60)}h {(entry.durationMinutes || 0) % 60}m</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Manual Time Entry */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Add Manual Time Entry</h4>
                  <form onSubmit={handleManualTimeEntry} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        value={manualTimeForm.description}
                        onChange={(e) => setManualTimeForm(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="What work was done?"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={manualTimeForm.durationMinutes}
                          onChange={(e) => setManualTimeForm(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="30"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Activity Type
                        </label>
                        <select 
                          value={manualTimeForm.activityType}
                          onChange={(e) => setManualTimeForm(prev => ({ ...prev, activityType: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="development">Development</option>
                          <option value="research">Research</option>
                          <option value="communication">Communication</option>
                          <option value="testing">Testing</option>
                          <option value="deployment">Deployment</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="manual-billable"
                        checked={manualTimeForm.isBillable}
                        onChange={(e) => setManualTimeForm(prev => ({ ...prev, isBillable: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="manual-billable" className="ml-2 text-sm text-gray-700">
                        Billable
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isAddingManualTime}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {isAddingManualTime ? 'Adding...' : 'Add Time Entry'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default TicketDetail
